// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import type { TableOptions } from '@tanstack/react-table';
// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import { flexRender, useTable } from '@tanstack/react-table';
import { isUndefined, omitBy } from 'lodash-es';
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, SearchIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '../containers/dialog.tsx';
import { ToggleGroup } from '../containers/toggle-group.tsx';
import { Menu, MenuItem, MenuItemLabel, MenuItemLeading } from '../primitives/menu.tsx';
import type { TableColumnLayout } from '../primitives/responsive-table.tsx';
import {
    List,
    ListHeader,
    ListHeaderLabel,
    ListPlaceholder,
    ListRow,
    ListRowEnd,
    ListRowStart,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeadContent,
    TableHeader,
    TableRow,
    TableSortButton,
    TableSortIcon,
} from '../primitives/responsive-table.tsx';
import { Spinner } from '../primitives/spinner.tsx';
import { ToggleButton } from '../primitives/toggle-button.tsx';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import type {
    Column,
    ColumnDef,
    ColumnMeta,
    ColumnPinningState,
    ColumnSort,
    OnChangeFn,
    Row,
    RowData,
    SortingState,
} from './data-table.tsx';
import { dataTableFeatures, resolveColumnId } from './data-table.tsx';
import { EmptyState, EmptyStateIcon, EmptyStateTitle } from './empty-state.tsx';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from './info-hint.tsx';

type TableFeatures<TSummary = never, TExtraColumnMeta extends object = object> = Omit<
    typeof dataTableFeatures,
    'columnMeta'
> & {
    columnMeta: ColumnMeta<TSummary> & TExtraColumnMeta;
};

type SupportedTableOptions<TData extends RowData, TSummary, TExtraColumnMeta extends object> = Pick<
    TableOptions<TableFeatures<TSummary, TExtraColumnMeta>, TData>,
    | 'defaultColumn'
    | 'enableMultiRowSelection'
    | 'globalFilterFn'
    | 'manualSorting'
    | 'onGlobalFilterChange'
    | 'onRowSelectionChange'
> & {
    state?: Omit<
        NonNullable<TableOptions<TableFeatures<TSummary, TExtraColumnMeta>, TData>['state']>,
        'sorting'
    >;
};

export type ResponsiveDataTableProps<
    TColumnId extends string,
    TData extends RowData,
    TSummary,
    TExtraColumnMeta extends object,
> = SupportedTableOptions<TData, TSummary, TExtraColumnMeta> &
    Omit<ComponentPropsWithRef<'table'>, 'summary'> & {
        columns: readonly ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>[];
        data: readonly TData[] | undefined;
        emptyLabel: ReactNode;
        isLoading?: boolean;
        onRowClick?: (row: Row<TData, TSummary, TExtraColumnMeta>) => void;
        summary?: TSummary;
        dimNonSelectedRows?: boolean;
        isFetchingNextPage?: boolean;
        hasNextPage?: boolean;
        onFetchNextPage?: () => void;
        sorting?: ColumnSort<TColumnId>;
        onSortingChange?: (sort: ColumnSort<TColumnId> | undefined) => void;
        sortSheetTitle: string;
        sortAscendingLabel: string;
        sortDescendingLabel: string;
        sortSheetCloseLabel: string;
        disableHeader?: boolean;
    };

const emptyData: [] = [];

export function ResponsiveDataTable<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
>({
    emptyLabel,
    isLoading = false,
    onRowClick,
    data,
    isFetchingNextPage,
    onFetchNextPage,
    hasNextPage,
    summary,
    dimNonSelectedRows = false,
    columns,
    state,
    defaultColumn,
    enableMultiRowSelection,
    globalFilterFn,
    manualSorting,
    onGlobalFilterChange,
    onRowSelectionChange,
    sorting,
    onSortingChange,
    sortSheetTitle,
    sortAscendingLabel,
    sortDescendingLabel,
    sortSheetCloseLabel,
    disableHeader = false,
    ref,
    className,
    ...tableProps
}: ResponsiveDataTableProps<TColumnId, TData, TSummary, TExtraColumnMeta>): ReactElement {
    const isSortingControlled = onSortingChange !== undefined;

    const enabledColumns = useMemo(
        () =>
            columns
                .filter((column) => column.meta?.enabled !== false)
                .map((column) => ({ ...column, enableSorting: column.enableSorting === true })),
        [columns],
    );

    const sortingState = useMemo<SortingState | undefined>(() => {
        if (sorting === undefined) {
            return isSortingControlled ? [] : undefined;
        }

        const isSortable = enabledColumns.some(
            (column) => resolveColumnId<TColumnId>(column) === sorting.id && column.enableSorting,
        );

        return isSortable ? [{ id: sorting.id, desc: sorting.direction === 'desc' }] : [];
    }, [enabledColumns, isSortingControlled, sorting]);

    const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>([]);
    const activeSorting = sortingState ?? uncontrolledSorting;

    const handleSortingChange: OnChangeFn<SortingState> =
        onSortingChange === undefined
            ? setUncontrolledSorting
            : (updater) => {
                  const [next] = typeof updater === 'function' ? updater(activeSorting) : updater;
                  const columnId = enabledColumns
                      .map((column) => resolveColumnId<TColumnId>(column))
                      .find((id) => id === next?.id);

                  onSortingChange(
                      next && columnId !== undefined
                          ? { id: columnId, direction: next.desc ? 'desc' : 'asc' }
                          : undefined,
                  );
              };

    const columnPinning = useColumnPinning(enabledColumns);

    const isMobile = !useBreakpoint('sm');
    const [isSortSheetOpen, setSortSheetOpen] = useState(false);

    const table = useTable<TableFeatures<TSummary, TExtraColumnMeta>, TData>({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- It's safe because columnMeta is just a type helper
        features: dataTableFeatures as TableFeatures<TSummary, TExtraColumnMeta>,
        ...omitBy(
            {
                defaultColumn,
                enableMultiRowSelection,
                globalFilterFn,
                manualSorting: manualSorting ?? (isSortingControlled || undefined),
                onGlobalFilterChange,
                onRowSelectionChange,
                onSortingChange: handleSortingChange,
            },
            isUndefined,
        ),
        columns: enabledColumns,
        state: {
            columnPinning,
            sorting: activeSorting,
            ...omitBy(state, isUndefined),
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- readonly widening on data prop
        data: (data ?? emptyData) as TData[],
    });

    const rows = table.getRowModel().rows;
    const hasSelectedVisibleRow = table.getSelectedRowModel().rows.length > 0;

    const summaryRow = useMemo(() => {
        if (summary === undefined) {
            return undefined;
        }

        const cells: Record<string, ReactNode> = {};
        for (const column of enabledColumns) {
            if (column.meta?.summary !== undefined) {
                cells[resolveColumnId<TColumnId>(column)] = column.meta.summary(summary);
            }
        }
        return cells;
    }, [enabledColumns, summary]);

    const leafHeaders = table.getHeaderGroups().at(-1)?.headers ?? [];
    const emptyState = (
        <EmptyState className="min-h-[50dvh] justify-center">
            <EmptyStateIcon>
                <SearchIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>{emptyLabel}</EmptyStateTitle>
        </EmptyState>
    );
    const isRowDimmed = (row: Row<TData, TSummary, TExtraColumnMeta>): boolean =>
        dimNonSelectedRows && hasSelectedVisibleRow && !row.getIsSelected();
    const showLoadMore = hasNextPage === true && !isLoading && rows.length > 0;
    const { ref: loadMoreRef } = useInView({
        skip: !showLoadMore || isFetchingNextPage,
        onChange: (inView) => {
            if (inView) {
                onFetchNextPage?.();
            }
        },
    });

    if (isMobile) {
        const sortableHeaders = leafHeaders.filter((header) => header.column.getCanSort());
        const sortedColumn = sortableHeaders.find((header) => header.column.getIsSorted() !== false)?.column;
        const sortedDirection = sortedColumn?.getIsSorted() || undefined;
        const isHidden = (header: (typeof leafHeaders)[number]): boolean =>
            header.column.getIsPinned() === false;
        const showSummary =
            summaryRow !== undefined &&
            !isLoading &&
            rows.length > 0 &&
            leafHeaders.some(
                (header, index) => index > 0 && !isHidden(header) && header.column.id in summaryRow,
            );

        return (
            <List className={className}>
                {!disableHeader && (
                    <ListHeader
                        disabled={sortableHeaders.length === 0}
                        onClick={() => {
                            setSortSheetOpen(true);
                        }}
                    >
                        {leafHeaders.map((header) => {
                            if (isHidden(header)) {
                                return undefined;
                            }

                            return (
                                <ListHeaderLabel
                                    key={header.id}
                                    direction={header.column === sortedColumn ? sortedDirection : undefined}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </ListHeaderLabel>
                            );
                        })}
                    </ListHeader>
                )}

                {showSummary && (
                    <ListRow className="border-b-2 border-line-weak font-medium">
                        {leafHeaders.map((header, index) => {
                            if (isHidden(header)) {
                                return undefined;
                            }

                            return index === 0 ? (
                                <ListRowStart key={header.id}>{summaryRow[header.column.id]}</ListRowStart>
                            ) : (
                                <ListRowEnd key={header.id}>{summaryRow[header.column.id]}</ListRowEnd>
                            );
                        })}
                    </ListRow>
                )}

                {!isLoading &&
                    rows.map((row) => {
                        const cells = row.getAllCellsByColumnId();

                        return (
                            <ListRow
                                key={row.id}
                                data-state={row.getIsSelected() ? 'selected' : undefined}
                                className={isRowDimmed(row) ? 'opacity-50' : undefined}
                                onClick={
                                    onRowClick
                                        ? () => {
                                              onRowClick(row);
                                          }
                                        : undefined
                                }
                            >
                                {leafHeaders.map((header, index) => {
                                    const cell = cells[header.column.id];
                                    if (isHidden(header) || cell === undefined) {
                                        return undefined;
                                    }

                                    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                                    return index === 0 ? (
                                        <ListRowStart key={header.id}>{content}</ListRowStart>
                                    ) : (
                                        <ListRowEnd key={header.id}>{content}</ListRowEnd>
                                    );
                                })}
                            </ListRow>
                        );
                    })}

                {isLoading ? (
                    <ListPlaceholder>
                        <Spinner />
                    </ListPlaceholder>
                ) : null}

                {showLoadMore ? (
                    <ListPlaceholder ref={loadMoreRef} className="h-32">
                        <Spinner />
                    </ListPlaceholder>
                ) : null}

                {!isLoading && rows.length === 0 ? emptyState : null}

                {!disableHeader && (
                    <Dialog open={isSortSheetOpen} onOpenChange={setSortSheetOpen}>
                        <DialogContent variant="picker">
                            <DialogHeader>
                                <DialogTitle>{sortSheetTitle}</DialogTitle>
                                <DialogIconClose label={sortSheetCloseLabel} />
                            </DialogHeader>
                            <DialogBody className="flex flex-col gap-cozy">
                                <ToggleGroup
                                    className="w-full"
                                    selected={sortedDirection}
                                    onSelectedChange={(direction) => {
                                        (sortedColumn ?? sortableHeaders[0]?.column)?.toggleSorting(
                                            direction === 'desc',
                                        );
                                    }}
                                >
                                    <ToggleButton optionValue="asc" className="flex-1">
                                        <ArrowUpIcon className="size-4" />
                                        {sortAscendingLabel}
                                    </ToggleButton>
                                    <ToggleButton optionValue="desc" className="flex-1">
                                        <ArrowDownIcon className="size-4" />
                                        {sortDescendingLabel}
                                    </ToggleButton>
                                </ToggleGroup>
                                <Menu className="p-0">
                                    {sortableHeaders.map((header) => {
                                        const isSorted = header.column === sortedColumn;

                                        return (
                                            <MenuItem
                                                key={header.id}
                                                role="menuitemradio"
                                                aria-checked={isSorted}
                                                onClick={() => {
                                                    header.column.toggleSorting(sortedDirection === 'desc');
                                                }}
                                            >
                                                <MenuItemLeading>
                                                    <CheckIcon
                                                        className={cn('text-brand', !isSorted && 'invisible')}
                                                        strokeWidth={2.5}
                                                    />
                                                </MenuItemLeading>
                                                <MenuItemLabel>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext(),
                                                    )}
                                                </MenuItemLabel>
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </DialogBody>
                        </DialogContent>
                    </Dialog>
                )}
            </List>
        );
    }

    const showSummary = summaryRow !== undefined && !isLoading && rows.length > 0;

    const columnLayout = (column: Column<TData, TSummary, TExtraColumnMeta>): TableColumnLayout => {
        const pinned = column.getIsPinned() || undefined;
        return {
            pinned,
            pinOffset: pinned === 'end' ? column.getAfter('end') : column.getStart('start'),
            size: column.getSize(),
        };
    };

    return (
        <TableContainer className={className}>
            <Table
                {...tableProps}
                ref={ref}
                style={{ minWidth: rows.length === 0 ? undefined : table.getCenterTotalSize() }}
            >
                {!disableHeader && (
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const { column } = header;
                                    const label = flexRender(column.columnDef.header, header.getContext());
                                    const meta = column.columnDef.meta;
                                    const hint =
                                        meta?.hint === undefined ? undefined : (
                                            <InfoHint
                                                onOpenChange={(open) => {
                                                    if (open) {
                                                        meta.onHintOpen?.();
                                                    }
                                                }}
                                            >
                                                <InfoHintTrigger className="-mx-1.5" />
                                                <InfoHintContent side="bottom">
                                                    <InfoHintHeader>
                                                        <InfoHintTitle>{label}</InfoHintTitle>
                                                    </InfoHintHeader>
                                                    <InfoHintBody>{meta.hint}</InfoHintBody>
                                                </InfoHintContent>
                                            </InfoHint>
                                        );
                                    const sorted = column.getIsSorted();
                                    const direction = sorted === false ? undefined : sorted;
                                    const toggleDirection = (): void => {
                                        column.toggleSorting(direction === 'asc');
                                    };
                                    const icon = (
                                        <TableSortIcon direction={direction} onClick={toggleDirection} />
                                    );

                                    return (
                                        <TableHead
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...columnLayout(column)}
                                            align={meta?.align === 'right' ? 'right' : 'left'}
                                            sortDirection={column.getCanSort() ? sorted : undefined}
                                        >
                                            {header.isPlaceholder ? null : column.getCanSort() ? (
                                                <TableHeadContent>
                                                    {meta?.align === 'right' && icon}
                                                    <TableSortButton onClick={toggleDirection}>
                                                        {label}
                                                    </TableSortButton>
                                                    {hint}
                                                    {meta?.align !== 'right' && icon}
                                                </TableHeadContent>
                                            ) : (
                                                <TableHeadContent>
                                                    {label}
                                                    {hint}
                                                </TableHeadContent>
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                )}

                <TableBody>
                    {showSummary && (
                        <TableRow className="hover:bg-container">
                            {leafHeaders.map(({ column }) => (
                                <TableCell
                                    key={column.id}
                                    align={column.columnDef.meta?.align === 'right' ? 'right' : 'left'}
                                    {...columnLayout(column)}
                                    className="border-t border-b-2 border-t-line-weakest border-b-line-weak font-medium"
                                >
                                    {summaryRow[column.id]}
                                </TableCell>
                            ))}
                        </TableRow>
                    )}

                    {!isLoading &&
                        rows.map((row) => {
                            const isDimmed = isRowDimmed(row);

                            return (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    data-dimmed={isDimmed ? 'true' : undefined}
                                    onClick={
                                        onRowClick
                                            ? () => {
                                                  onRowClick(row);
                                              }
                                            : undefined
                                    }
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const meta = cell.column.columnDef.meta;
                                        return (
                                            <TableCell
                                                key={cell.id}
                                                data-state={row.getIsSelected() && 'selected'}
                                                {...columnLayout(cell.column)}
                                                dimmed={isDimmed && !meta?.preserveOpacityWhenDimmed}
                                                align={meta?.align === 'right' ? 'right' : 'left'}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}

                    {isLoading ? (
                        <TableRow className="hover:bg-container">
                            <TableCell colSpan={leafHeaders.length} align="center" className="h-96">
                                <Spinner className="inline-block" />
                            </TableCell>
                        </TableRow>
                    ) : null}

                    {!isLoading && rows.length === 0 ? (
                        <TableRow className="hover:bg-container">
                            <TableCell colSpan={leafHeaders.length} className="p-0">
                                {emptyState}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {showLoadMore ? (
                        <TableRow ref={loadMoreRef} className="hover:bg-container">
                            <TableCell colSpan={leafHeaders.length} align="center" className="h-32">
                                <Spinner className="inline-block" />
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function useColumnPinning<
    TColumnId extends string,
    TData extends RowData,
    TSummary,
    TExtraColumnMeta extends object,
>(columns: ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>[]): ColumnPinningState {
    return useMemo(() => {
        const start: string[] = [];
        const end: string[] = [];
        for (const [index, column] of columns.entries()) {
            const side = column.meta?.pinned ?? (index === 0 ? 'left' : undefined);
            if (side !== undefined) {
                (side === 'left' ? start : end).push(resolveColumnId<TColumnId>(column));
            }
        }
        return { start, end };
    }, [columns]);
}
